import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ChevronLeft,
    File,
    FileText,
    FolderPlus,
    Link2,
    StickyNote,
    Package,
    Pencil,
    Plus,
    ScanLine,
} from 'lucide-react';
import { Button } from '../../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { FolderDetail } from '../../../services/api/folderService';
import { PinButton } from '../PinButton';
import { CategoryChip } from '../../ui-components/category/CategoryChip';

export type FolderAddTarget =
    | 'implants'
    | 'scanners'
    | 'policies'
    | 'files'
    | 'link'
    | 'note'
    | 'subfolder';

const ADD_OPTIONS: {
    target: FolderAddTarget;
    labelKey: string;
    Icon: typeof Plus;
}[] = [
    {
        target: 'implants',
        labelKey: 'folders.detail.actions.addImplants',
        Icon: Package,
    },
    {
        target: 'scanners',
        labelKey: 'folders.detail.actions.linkScanners',
        Icon: ScanLine,
    },
    {
        target: 'policies',
        labelKey: 'folders.detail.actions.addPolicies',
        Icon: FileText,
    },
    {
        target: 'files',
        labelKey: 'folders.detail.actions.addFiles',
        Icon: File,
    },
    { target: 'link', labelKey: 'folders.links.add', Icon: Link2 },
    { target: 'note', labelKey: 'folders.note.add', Icon: StickyNote },
    {
        target: 'subfolder',
        labelKey: 'folders.detail.newSubFolder',
        Icon: FolderPlus,
    },
];

interface FolderHeaderProps {
    folder: FolderDetail;
    canManage: boolean;
    pinPending: boolean;
    onTogglePin: () => void;
    onOpenEdit: () => void;
    onOpenDelete: () => void;
    onAdd: (target: FolderAddTarget) => void;
}

export const FolderHeader = ({
    folder,
    canManage,
    pinPending,
    onTogglePin,
    onOpenEdit,
    onOpenDelete,
    onAdd,
}: FolderHeaderProps) => {
    const { t } = useTranslation();
    const isSubFolder = !!folder.parent;
    const addOptions = ADD_OPTIONS.filter(
        ({ target }) =>
            (target !== 'subfolder' || !isSubFolder) &&
            (target !== 'note' || !folder.note)
    );

    return (
        <>
            {folder.parent ? (
                <nav
                    aria-label={t('folders.detail.path')}
                    className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-subtle-foreground"
                >
                    <Link to="/" className="hover:text-foreground">
                        {t('folders.overview.sections.all')}
                    </Link>
                    <span aria-hidden="true">/</span>
                    <Link
                        to={`/group/${folder.parent._id}`}
                        className="hover:text-foreground"
                    >
                        {folder.parent.title}
                    </Link>
                    <span aria-hidden="true">/</span>
                </nav>
            ) : (
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-xs text-subtle-foreground hover:text-foreground"
                >
                    <ChevronLeft className="h-3 w-3" aria-hidden="true" />
                    {t('folders.overview.sections.all')}
                </Link>
            )}

            <header className="mb-3 mt-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 flex-col gap-2">
                        <h1 className="min-w-0 break-words text-2xl font-medium tracking-normal text-zinc-100">
                            {folder.title}
                        </h1>
                        {folder.category && (
                            <CategoryChip
                                category={folder.category}
                                className="self-start"
                            />
                        )}
                    </div>
                    <div className="flex flex-shrink-0 flex-wrap items-center gap-2.5">
                        {canManage && !isSubFolder && (
                            <PinButton
                                isPinned={folder.isPinned}
                                onToggle={onTogglePin}
                                disabled={pinPending}
                            />
                        )}
                        {canManage && (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={onOpenEdit}
                                >
                                    <Pencil
                                        className="mr-2 h-4 w-4"
                                        aria-hidden="true"
                                    />
                                    {t('folders.detail.editFolder')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-auto px-2 py-1 text-xs font-medium text-subtle-foreground hover:bg-transparent hover:text-destructive"
                                    onClick={onOpenDelete}
                                >
                                    {t('folders.detail.delete')}
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button size="sm">
                                            <Plus
                                                className="mr-2 h-4 w-4"
                                                aria-hidden="true"
                                            />
                                            {t('folders.detail.actions.add')}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        {addOptions.map(
                                            ({ target, labelKey, Icon }) => (
                                                <DropdownMenuItem
                                                    key={target}
                                                    onSelect={() =>
                                                        onAdd(target)
                                                    }
                                                >
                                                    <Icon
                                                        className="mr-2 h-4 w-4 text-subtle-foreground"
                                                        aria-hidden="true"
                                                    />
                                                    {t(labelKey)}
                                                </DropdownMenuItem>
                                            )
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        )}
                    </div>
                </div>
            </header>
        </>
    );
};
